import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { LoadingIcon, TopDropdown } from "../../../../components";
import { getGreenhouses } from "../../../../utils/api";
import { UserContext } from "../../../../context/UserContext";
import { useLocalStorage } from "../../../../hooks";
import { DatePicker } from "antd";

const VisualisationDropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentDate,
  setCurrentDate,
  plotData,
  setPlotData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  dateData,
  setDateData,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Block");

  const [plotLoading, setPlotLoading] = useState(false);
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  const getPlotDates = (plot) => (plot?.harvests || []).map((h) => h.date);

  const findPlot = (plotId, greenhouse = currentGreenhouse) =>
    (greenhouse?.plots || plotData || []).find(
      (plot) => plot.id === plotId && (!plot.greenhouse_id || plot.greenhouse_id === greenhouse?.id),
    ) ||
    (greenhouse?.plots || plotData || []).find((plot) => plot.id === plotId);

  const syncPlotSelection = (plot, greenhouse = currentGreenhouse, preferredDate = null) => {
    if (!plot) return;
    const dates = getPlotDates(plot);
    const nextDate = preferredDate && dates.includes(preferredDate) ? preferredDate : dates[0] || null;

    setCurrentPlot(plot);
    setDateData(dates);
    setCurrentDate(nextDate);

    const params = new URLSearchParams(searchParams);
    if (greenhouse) params.set("greenhouse", greenhouse.id);
    params.set("plot", plot.id);
    if (nextDate) params.set("date", nextDate);
    else params.delete("date");
    setSearchParams(params);

    const orgObj = defaultDropdown?.[currentOrg] || {};
    setDefaultDropdown({
      ...defaultDropdown,
      [currentOrg]: {
        ...orgObj,
        greenhouse: greenhouse?.id || orgObj.greenhouse,
        plot: plot.id,
        date: nextDate || orgObj.date,
      },
    });
  };

  // ---------------------------------------------
  //  fetchGreenhouses
  // ---------------------------------------------
  const fetchGreenhouses = async (orgId, siteId, cropId = null) => {
    setGreenhouseLoading(true);
    const res = await getGreenhouses(orgId, siteId, cropId);
    const greenhouseList = Array.isArray(res?.data?.data)
      ? res.data.data
      : [];
    const data = greenhouseList.map((gh) => ({
      name: gh.name,
      id: gh.id,
      plots: (gh.plots || []).map((plot) => ({
        ...plot,
        greenhouse_id: gh.id,
        greenhouse_name: gh.name,
      })),
    }));
    setGreenhouseData(data);
    setGreenhouseLoading(false);
  };

  // ---------------------------------------------
  //  disable date logic
  // ---------------------------------------------
  const disabledDate = (current) => {
    const dayjsDates = dateData.map((date) => dayjs(date));
    return (
      current &&
      !dayjsDates.reduce((isEnabled, date) => {
        if (isEnabled) {
          return true;
        } else {
          return date.isSame(current, "day");
        }
      }, false)
    );
  };

  const handleDateChange = (date, dateString) => {
    if (dateString) {
      setCurrentDate(dateString);
      const params = new URLSearchParams(searchParams);
      params.set("date", dateString);
      setSearchParams(params);
    }
  };

  // ---------------------------------------------
  //  On site/crop => fetch greenhouses
  // ---------------------------------------------
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop) {
      const siteMeta = currentSite?.metadata?.[currentCrop?.name] || {};
      setSiteMetadata(siteMeta);
      if (siteMeta?.greenhouse_name)
        setGreenHouseLabel(siteMeta.greenhouse_name);
      if (siteMeta?.block_name) setBlockLabel(siteMeta.block_name);

      fetchGreenhouses(currentOrg, currentSite.id, currentCrop.id);
    }
  }, [currentSite, currentCrop, currentOrg]);

  // ---------------------------------------------
  //  On greenhouseData => pick default greenhouse
  // ---------------------------------------------
  useEffect(() => {
    if (greenhouseData?.length) {
      const params = new URLSearchParams(searchParams);
      const ghIdParam = params.get("greenhouse");
      if (ghIdParam) {
        // from URL
        const foundGh = greenhouseData.find((g) => g.id === ghIdParam);
        if (foundGh) {
          setCurrentGreenhouse(foundGh);
          setPlotData(foundGh.plots);
        } else {
          setCurrentGreenhouse(greenhouseData[0]);
          setPlotData(greenhouseData[0].plots);
        }
      } else if (!settingDefaults.greenhouse) {
        const storedGhId = defaultDropdown?.[currentOrg]?.greenhouse;
        if (storedGhId) {
          const foundGh = greenhouseData.find((g) => g.id === storedGhId);
          if (foundGh) {
            setCurrentGreenhouse(foundGh);
            setPlotData(foundGh.plots);
          } else {
            setCurrentGreenhouse(greenhouseData[0]);
            setPlotData(greenhouseData[0].plots);
          }
        } else {
          setCurrentGreenhouse(greenhouseData[0]);
          setPlotData(greenhouseData[0].plots);
        }
        setSettingDefaults({ ...settingDefaults, greenhouse: true });
      } else {
        setCurrentGreenhouse(greenhouseData[0]);
        setPlotData(greenhouseData[0].plots);
      }
    } else {
      // no greenhouse
      setCurrentGreenhouse(null);
      setPlotData(null);
    }
  }, [greenhouseData]);

  // if greenhouse changes => set plot data
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop && currentGreenhouse) {
      setPlotData(currentGreenhouse.plots);
    }
  }, [currentGreenhouse]);

  // ---------------------------------------------
  //  If plot changes => set date data
  // ---------------------------------------------
  useEffect(() => {
    if (currentPlot) {
      setDateData(getPlotDates(currentPlot));
    }
  }, [currentPlot, setDateData]);

  // ---------------------------------------------
  //  On plotData => pick default plot
  // ---------------------------------------------
  useEffect(() => {
    if (plotData?.length) {
      const params = new URLSearchParams(searchParams);
      const plotIdParam = params.get("plot");
      const dateParam = params.get("date");

      if (plotIdParam) {
        const foundPlot = findPlot(plotIdParam);
        if (foundPlot) {
          syncPlotSelection(foundPlot, currentGreenhouse, dateParam);
        } else {
          syncPlotSelection(plotData[0], currentGreenhouse, dateParam);
        }
      } else if (!settingDefaults.plot) {
        const storedPlotId = defaultDropdown?.[currentOrg]?.plot;
        const storedDate = defaultDropdown?.[currentOrg]?.date;
        if (storedPlotId) {
          const foundPlot = findPlot(storedPlotId);
          if (foundPlot) {
            syncPlotSelection(foundPlot, currentGreenhouse, storedDate);
          } else {
            syncPlotSelection(plotData[0], currentGreenhouse, storedDate);
          }
        } else {
          syncPlotSelection(plotData[0], currentGreenhouse, storedDate);
        }
        setSettingDefaults({ ...settingDefaults, plot: true });
      } else {
        syncPlotSelection(plotData[0], currentGreenhouse);
      }
    } else {
      setCurrentPlot(null);
      setDateData(null);
    }
  }, [plotData]);

  // ---------------------------------------------
  //  On dateData => pick default date
  // ---------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const dateFromUrl = params.get("date");

    if (dateFromUrl) {
      if (dateData?.includes(dateFromUrl)) {
        setCurrentDate(dateFromUrl);
      } else {
        const firstDate = dateData?.[0] || null;
        setCurrentDate(firstDate);
      }
    } else {
      const firstDate = dateData?.[0] || null;
      setCurrentDate(firstDate);
    }
  }, [dateData]);

  // ---------------------------------------------
  //  If greenhouse changes => update param + localStorage
  // ---------------------------------------------
  useEffect(() => {
    if (currentGreenhouse) {
      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouse: currentGreenhouse.id,
        },
      });
    }
  }, [currentGreenhouse]);

  // if plot changes => param + localStorage
  useEffect(() => {
    if (currentPlot) {
      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          plot: currentPlot.id,
        },
      });
    }
  }, [currentPlot]);

  // ---------------------------------------------
  //  overall loading
  // ---------------------------------------------
  useEffect(() => {
    setDropdownLoading(
      orgLoading ||
        siteLoading ||
        cropLoading ||
        plotLoading ||
        greenhouseLoading ||
        !currentDate,
    );
  }, [
    orgLoading,
    cropLoading,
    siteLoading,
    plotLoading,
    greenhouseLoading,
    currentDate,
  ]);

  return (
    <>
      {orgLoading || siteLoading || plotLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />
          <TopDropdown
            label={`Select ${greenHouseLabel}:`}
            val={currentGreenhouse?.id ?? undefined}
            placeholder={`Select ${greenHouseLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(ghId) => {
              const ghObj = greenhouseData.find((g) => g.id === ghId);
              setCurrentGreenhouse(ghObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              if (!greenhouseData) return [];
              const allNamesAreNumeric = greenhouseData.every((gh) =>
                /^\d+$/.test(gh.name),
              );

              const sorted = [...greenhouseData];
              if (allNamesAreNumeric) {
                sorted.sort((a, b) => parseInt(a.name) - parseInt(b.name));
              } else {
                sorted.sort((a, b) => a.name.localeCompare(b.name));
              }

              return sorted.map((greenhouse) => ({
                value: greenhouse.id,
                label: greenhouse.name,
              }));
            })()}
          />
          <TopDropdown
            label={`Select ${blockLabel}:`}
            val={
              currentGreenhouse && currentPlot
                ? `${currentGreenhouse.id}---${currentPlot.id}`
                : undefined
            }
            placeholder={`Select ${blockLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(value) => {
              const [greenhouseId, plotId] = value.split("---");
              const greenhouse = greenhouseData.find((g) => g.id === greenhouseId);
              syncPlotSelection(findPlot(plotId, greenhouse), greenhouse);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            data={(() => {
              if (!plotData) return [];
              const allNamesAreNumeric = plotData.every((p) =>
                /^\d+$/.test(p.name),
              );
              const sorted = [...plotData];
              if (allNamesAreNumeric) {
                sorted.sort((a, b) => parseInt(a.name) - parseInt(b.name));
              } else {
                sorted.sort((a, b) => a.name.localeCompare(b.name));
              }
              return sorted.map((plot) => ({
                label: plot.name,
                value: `${plot.greenhouse_id || currentGreenhouse?.id}---${plot.id}`,
              }));
            })()}
          />

          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Date:
            </div>
            <DatePicker
              value={currentDate ? dayjs(currentDate) : null}
              className="flex-1 min-w-[150px] sm:mb-2"
              disabledDate={disabledDate}
              onChange={handleDateChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VisualisationDropdowns;
